import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MapPin, Landmark, Scale } from "lucide-react";
import { CONTACT } from "@/lib/siteData";
import { RevealLines, FadeUp } from "./Shared";

const items = [
  { icon: Landmark, k: "District Court", v: "Purnia, Bihar" },
  { icon: Scale, k: "Civil Court", v: "Original & Appellate Side" },
  { icon: MapPin, k: "Revenue Courts", v: "Collectorate & DCLR, Purnia" },
];

export const Jurisdiction = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section ref={ref} id="jurisdiction" data-testid="jurisdiction-section" className="relative px-6 md:px-12 py-24 md:py-40 overflow-hidden">
      <motion.div style={{ y, scale: 1.15 }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Scales of justice"
          className="w-full h-full object-cover grayscale-img opacity-[0.12]"
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1500px]">
        <FadeUp className="flex items-baseline gap-4 mb-16 md:mb-24 border-b border-[var(--line)] pb-6">
          <span className="font-mono-plex text-xs text-[var(--muted)]">03</span>
          <span className="font-mono-plex text-xs uppercase tracking-[0.28em] text-[var(--muted)]">Court & Jurisdiction</span>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <RevealLines
              tag="h2"
              className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.92]"
              lines={["Practising before", "the courts of", "Purnia district."]}
            />
          </div>
          <FadeUp delay={0.1} className="lg:col-span-5 lg:pt-4 flex items-end">
            <p className="text-base md:text-lg text-[var(--off)]/75 leading-relaxed">
              Matters are conducted before the District & Civil Court at Purnia and the revenue authorities of the district — with {CONTACT.experience} years of familiarity with local procedure and the bench.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)] mt-16 border border-[var(--line)]">
          {items.map((it, i) => (
            <motion.div
              key={it.k}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-[#060606]/90 backdrop-blur-sm p-8 md:p-10"
            >
              <it.icon size={26} strokeWidth={1} className="mb-8 text-[var(--off)]" />
              <div className="font-serif-display text-2xl md:text-3xl mb-2">{it.k}</div>
              <div className="font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{it.v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
